import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation, A11y } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { fetchPartnerById } from '@/services/partnerService';
import { fetchProducts } from '@/services/productService';
import { useLanguage } from '@/context/LanguageContext';
import { Product } from '@/types/product';
import { ArrowLeft } from 'lucide-react';

const PartnerProjects: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const projectIdParam = searchParams.get('project');
  const { tl } = useLanguage();
  const navigate = useNavigate();
  const [galleryIndex, setGalleryIndex] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    setGalleryIndex(0);
  }, [id, projectIdParam]);

  const { data: partner, isLoading } = useQuery({
    queryKey: ['partner', id],
    queryFn: () => fetchPartnerById(id!),
    enabled: !!id,
  });

  const project = useMemo(() => {
    const projects = partner?.projects || [];
    if (!projects.length) return undefined;
    if (projectIdParam) {
      return projects.find((p) => p.id === projectIdParam) || projects[0];
    }
    return projects[0];
  }, [partner?.projects, projectIdParam]);

  const { data: projectProducts = [], isLoading: productsLoading } = useQuery<Product[]>({
    queryKey: ['projectProducts', project?.id, project?.products],
    queryFn: async () => {
      if (!project?.products?.length) return [];
      const allProducts = await fetchProducts({});
      const names = project.products;
      return allProducts.filter((product) =>
        names.some(
          (name) =>
            product.name === name ||
            product.name.toLowerCase() === name.toLowerCase() ||
            product.name.includes(name) ||
            name.includes(product.name)
        )
      );
    },
    enabled: !!project?.products?.length,
  });

  const images = project?.images || [];
  const heroImage = images[0] || partner?.logo || '';
  const galleryImages = images.length > 1 ? images.slice(1) : images.length === 1 ? [] : [];

  const categoryLabel =
    project?.project_category && project.project_category !== 'All'
      ? project.project_category
      : tl('Projet', 'Project', 'Progetto');

  const locationLabel = project?.location || partner?.location;

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-neutral-300 border-t-black" />
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-white px-4 text-center">
        <p className="text-lg text-neutral-500">
          {tl('Partenaire non trouvé', 'Partner not found', 'Partner non trovato')}
        </p>
        <button
          type="button"
          onClick={() => navigate('/projects')}
          className="inline-flex items-center text-sm uppercase tracking-wider text-black underline underline-offset-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {tl('Retour aux projets', 'Back to projects', 'Torna ai progetti')}
        </button>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-white text-black">
      <Helmet>
        <title>
          {project?.title || partner.name} | {tl('Projets', 'Projects', 'Progetti')} | Meubles Karim
        </title>
      </Helmet>

      {/* Hero: text left / first image right */}
      <section className="border-b border-neutral-100">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 items-start gap-8 px-4 pb-16 pt-[76px] md:gap-10 md:px-8 lg:grid-cols-2 lg:gap-12 lg:px-12 lg:pb-20 lg:pt-[96px]">
          <div className="order-2 lg:order-1 lg:pt-4">
            <p className="text-[11px] uppercase tracking-[0.16em] text-neutral-500">
              <Link to="/projects" className="transition-colors hover:text-black">
                {tl('Projets', 'Projects', 'Progetti')}
              </Link>
              <span className="mx-2 text-neutral-300">/</span>
              <span>{categoryLabel}</span>
              {project?.title ? (
                <>
                  <span className="mx-2 text-neutral-300">/</span>
                  <span className="text-neutral-700">{project.title}</span>
                </>
              ) : null}
            </p>

            <h1 className="mt-6 text-3xl font-semibold uppercase leading-[1.1] tracking-[0.06em] md:text-4xl lg:text-[2.75rem]">
              {project?.title || partner.name}
            </h1>

            <dl className="mt-8 grid max-w-md grid-cols-[auto_1fr] gap-x-8 gap-y-3 text-sm">
              <dt className="text-neutral-400">{tl('Catégorie', 'Category', 'Categoria')}</dt>
              <dd className="font-medium text-neutral-800">{categoryLabel}</dd>
              {locationLabel ? (
                <>
                  <dt className="text-neutral-400">{tl('Lieu', 'Location', 'Luogo')}</dt>
                  <dd className="font-medium text-neutral-800">{locationLabel}</dd>
                </>
              ) : null}
              <dt className="text-neutral-400">{tl('Partenaire', 'Partner', 'Partner')}</dt>
              <dd className="font-medium text-neutral-800">{partner.name}</dd>
            </dl>

            {project?.description ? (
              <div className="mt-10 max-w-xl space-y-4 text-[15px] leading-relaxed text-neutral-600">
                {project.description.split(/\n+/).filter(Boolean).map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            ) : partner.description ? (
              <p className="mt-10 max-w-xl text-[15px] leading-relaxed text-neutral-600">
                {partner.description}
              </p>
            ) : null}
          </div>

          <div className="order-1 lg:order-2">
            <div className="relative aspect-[3/4] w-full overflow-hidden bg-neutral-100 lg:aspect-[4/5]">
              {heroImage ? (
                <img
                  src={heroImage}
                  alt={project?.title || partner.name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-neutral-400">
                  {tl('Aucune image', 'No image', 'Nessuna immagine')}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Remaining images — horizontal scroll / swiper */}
      {galleryImages.length > 0 && (
        <section className="bg-white py-10 md:py-16">
          <div className="mx-auto max-w-[1400px] px-0 md:px-8 lg:px-12">
            <Swiper
              modules={[Pagination, Navigation, A11y]}
              slidesPerView={1}
              spaceBetween={0}
              pagination={{ clickable: true }}
              navigation
              onSlideChange={(swiper) => setGalleryIndex(swiper.activeIndex)}
              className="project-gallery-swiper !pb-12"
            >
              {galleryImages.map((image, index) => (
                <SwiperSlide key={`${image}-${index}`}>
                  <div className="relative aspect-[16/9] w-full overflow-hidden bg-neutral-100 md:aspect-[2.2/1]">
                    <img
                      src={image}
                      alt={`${project?.title || partner.name} ${index + 2}`}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
            <p className="mt-2 text-center text-[11px] uppercase tracking-[0.18em] text-neutral-400">
              {galleryIndex + 1} / {galleryImages.length}
            </p>
          </div>
        </section>
      )}

      {/* Related products */}
      <section className="border-t border-neutral-100 bg-white px-4 py-16 md:px-8 md:py-24 lg:px-12">
        <div className="mx-auto max-w-[1400px]">
          <h2 className="mb-12 text-center text-sm font-semibold uppercase tracking-[0.22em] text-black md:mb-16">
            {tl('Produits associés', 'Related products', 'Prodotti correlati')}
          </h2>

          {productsLoading ? (
            <div className="flex justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-300 border-t-black" />
            </div>
          ) : projectProducts.length > 0 ? (
            <div className="grid grid-cols-1 gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
              {projectProducts.map((product) => {
                const image =
                  product.images?.[0] || product.image_nobg || '/placeholder.svg';
                return (
                  <button
                    key={product.id}
                    type="button"
                    className="group text-left"
                    onClick={() => navigate(`/product/${product.id}`)}
                  >
                    <div className="relative aspect-square w-full overflow-hidden bg-neutral-100">
                      <img
                        src={image}
                        alt={product.name}
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                        loading="lazy"
                      />
                    </div>
                    <div className="mt-4 text-center">
                      <h3 className="text-sm font-semibold uppercase tracking-[0.12em]">
                        {product.name}
                      </h3>
                      <p className="mt-1 text-xs text-neutral-500">{product.category}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-center text-sm text-neutral-500">
              {tl(
                'Aucun produit associé à ce projet.',
                'No products linked to this project.',
                'Nessun prodotto collegato a questo progetto.'
              )}
            </p>
          )}
        </div>
      </section>

      <div className="border-t border-neutral-100 px-4 py-6 text-center text-[11px] uppercase tracking-[0.14em] text-neutral-400 md:px-8">
        <Link to="/" className="transition-colors hover:text-black">
          Meubles Karim
        </Link>
        <span className="mx-2">/</span>
        <Link to="/projects" className="transition-colors hover:text-black">
          {tl('Projets', 'Projects', 'Progetti')}
        </Link>
        {project?.title ? (
          <>
            <span className="mx-2">/</span>
            <span className="text-neutral-600">{project.title}</span>
          </>
        ) : null}
      </div>

      <style>{`
        .project-gallery-swiper .swiper-pagination-bullet {
          background: #a3a3a3;
          opacity: 1;
        }
        .project-gallery-swiper .swiper-pagination-bullet-active {
          background: #171717;
        }
        .project-gallery-swiper .swiper-button-next,
        .project-gallery-swiper .swiper-button-prev {
          color: #171717;
        }
        .project-gallery-swiper .swiper-button-next::after,
        .project-gallery-swiper .swiper-button-prev::after {
          font-size: 1.25rem;
        }
      `}</style>
    </main>
  );
};

export default PartnerProjects;
